/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {InfoPopUpComponent} from '../../../core/components/pop-ups/info-pop-up/info-pop-up.component';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {StateService} from '../../../core/state/state.service';
import {ExcelExportService} from '../../../services/excel-export.service';
import {VehicleScenarioService} from '../../../services/vehicle-scenario.service';

@Component({
    selector: 'app-update-vehicle-scenario-with-ext-data',
    templateUrl: './update-vehicle-scenario-with-ext-data.component.html',
    styleUrl: './update-vehicle-scenario-with-ext-data.component.scss',
    standalone: false
})
export class UpdateVehicleScenarioWithExtDataComponent implements OnInit {
  showSpinner: boolean;
  isUpdateComments: boolean;
  isUpdateDates: boolean;
  isPreprocess: boolean;

  file: File;
  sheets: string[];
  selectedSheet: string;

  constructor(public snackBar: FabMessageService,
              public readonly stateService: StateService,
              public readonly excelService: ExcelExportService,
              public readonly vehicleScenarioService: VehicleScenarioService,
              public readonly dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.isPreprocess = true;
    this.isUpdateDates = false;
    this.isUpdateComments = false;
  }

  /**
   * Uploads the selected file
   * @param event Map<string, any>
   */
  upload(event: any): void {
    this.sheets = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets,
      isMultiSelect: false
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.selectedSheet = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.file = null;
      },
      () => {
        if (this.selectedSheet.length === 0) {
          this.file = null;
        } else {
          this.openUpdateConfirmation();
        }
      }
    );
  }

  /**
   * Opens the update confirmation to continue with updating the vehicle scenario with external data
   */
  openUpdateConfirmation(): void {
    // Open confirmation dialog to confirm you want to continue with the update
    let message = 'This will update the scenario ' + this.stateService.selectedScenario.vehScenId + '- ' + this.stateService.selectedScenario.vehScenName + '. Continue?';
    if (this.isPreprocess) {
      message = 'This will preview the changes without making updates to the scenario ' + this.stateService.selectedScenario.vehScenId + '- ' + this.stateService.selectedScenario.vehScenName + '. Continue?';
    }
    this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmation',
        message: message,
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    }).afterClosed().subscribe({
      next: confirmation => {
        if (confirmation) {
          this.onConfirm()
        }
      }
    });
  }

  /**
   * Event handler for when user confirms the update
   */
  onConfirm(): void {
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const fileName = 'UpdateExDataVeh_' + this.stateService.selectedScenario.vehScenId + "_" + currDate;

    this.showSpinner = true;
    this.vehicleScenarioService.updateWithExternalData(this.file, this.selectedSheet, this.stateService.getUserGmid(),
      this.stateService.selectedScenario.vehScenId, this.isPreprocess, this.isUpdateDates, this.isUpdateComments).subscribe(
      {
        next: (data) => {
          this.excelService.exportToExcelFromServer(data, fileName);
        },
        error: (error) => {
          this.showSpinner = false;
          const info = error.replaceAll('. ', '.\n');
          this.showTemplateErrors(info);
        },
        complete: () => {
          this.showSpinner = false;
          this.snackBar.displaySuccess('Successfully retrieved data. Exporting...', DISMISS_MSG);
        }
      }
    );
  }

  /**
   * Shows the errors in the template format in a popup
   * @param info the errors to display to the user
   */
  showTemplateErrors(info: string): void {
    this.dialog.open(InfoPopUpComponent, {
      data: {
        title: 'Template Errors',
        info
      }
    });
  }
}
